import Alpine from "alpinejs";
import Split from "split.js";
import { HEAD_HEIGHT } from "../components/TreeNode";
import * as q from "../utils/qs";
import { TIMEOUT_SPLIT_TRANSITION_CLEANUP_MS } from "../timing";

interface SplitConfig {
  mode: "horizontal" | "vertical";
  step: number;
  persist: string | null;
}

function parseConfig(expression: string) {
  try {
    const config = JSON.parse(expression) as Partial<SplitConfig>;
    return {
      mode: config.mode ?? "vertical",
      step: config.step ?? HEAD_HEIGHT,
      persist: config.persist,
    };
  } catch (e) {
    console.warn("invalid split config", expression);
    throw e;
  }
}

function getComputedHeight(el: HTMLElement): number {
  const bh = getComputedStyle(el).height;

  // Remove the "px" at the end
  const bhv = parseFloat(bh.substring(0, bh.length - 2));
  return bhv;
}

function adjustHeights(
  step: number,
  top: HTMLElement,
  bottom: HTMLElement,
  persist: string | null = null,
  animate = false
) {
  const h = bottom.getBoundingClientRect().height;
  let r = h % step;
  if (r > step / 2) {
    r -= step;
  }

  const th = getComputedHeight(top);
  const nth = th + r;

  const bh = getComputedHeight(bottom);
  const nbh = bh - r;

  if (persist !== null) {
    q.setParam(persist, Math.round(nbh / step).toString());
  }

  if (!animate) {
    top.style.height = `${nth}px`;
    bottom.style.height = `${nbh}px`;
    return nbh;
  }

  requestAnimationFrame(() => {
    top.style.transition = "height 0.2s";
    bottom.style.transition = "height 0.2s";

    top.style.height = `${nth}px`;
    bottom.style.height = `${nbh}px`;

    window.setTimeout(() => {
      top.style.transition = "";
      bottom.style.transition = "";
    }, TIMEOUT_SPLIT_TRANSITION_CLEANUP_MS);
  });

  return nbh;
}

function getCurrentSizesAsRatio(
  top: HTMLElement,
  bottom: HTMLElement
): number[] {
  const topHeight = top.getBoundingClientRect().height;
  const bottomHeight = bottom.getBoundingClientRect().height;
  const height = topHeight + bottomHeight;

  return [100 * (topHeight / height), 100 * (bottomHeight / height)];
}

/**
 * Returns TRUE if the difference in pixel of the bottom is smaller than the step.
 * @param el Element containing the split
 * @param before Ratio (in percentage) of the height of the bottom element before drag
 * @param after Ratio (in percentage) of the height of the bottom element after drag
 * @param step Minimum of changes in pixel to considered the height of the bottom being changed
 */
function isUnchanged(
  el: HTMLElement,
  before: number,
  after: number,
  step: number
) {
  const height = el.getBoundingClientRect().height;
  const oldBottomHeight = before * height;
  const newBottomHeight = after * height;
  const delta = Math.abs(oldBottomHeight - newBottomHeight);
  return delta < step;
}

/**
 * Directive wrapper for SplitJS with automatic resizing
 * @param el Element to be invoked with SplitJS
 * @param direction Alpine directive, it's expression will be the SpitConfig
 * @param _
 */
const split: Alpine.DirectiveCallback = (el, directive, { cleanup }) => {
  const expression = directive.expression;

  const config = parseConfig(expression);
  const top = el.children[0] as HTMLElement;
  const gutter = el.children[1] as HTMLElement;
  const bottom = el.children[2] as HTMLElement;

  if (!top || !bottom || !gutter) {
    console.error(
      "Invalid configuration for x-split: could not find some elements"
    );
    return;
  }

  let bottomHeightPx = 0;
  let isDragging = false;

  let sizes = getCurrentSizesAsRatio(top, bottom);
  const instance = Split([top, bottom], {
    sizes,
    direction: config.mode ?? "vertical",
    minSize: [config.step * 4, config.step * 4],
    gutterSize: gutter.getBoundingClientRect().height,
    cursor: "grabbing",
    gutter: () => gutter,
    onDragStart(newSizes) {
      isDragging = true;
      sizes = newSizes;
    },
    onDragEnd(newSizes) {
      if (!isUnchanged(el, sizes[1], newSizes[1], config.step)) {
        bottomHeightPx = adjustHeights(
          config.step,
          top,
          bottom,
          config.persist,
          true
        );
      }
      isDragging = false;
    },
  });

  bottomHeightPx = adjustHeights(config.step, top, bottom, config.persist, false);

  const observer = new ResizeObserver(() => {
    if (isDragging) return;
    const containerHeight = el.getBoundingClientRect().height;
    const gutterHeight = gutter.getBoundingClientRect().height;
    const available = containerHeight - gutterHeight;
    const minSize = config.step * 4;
    const clampedBottom = Math.max(
      minSize,
      Math.min(bottomHeightPx, available - minSize)
    );
    top.style.height = `${available - clampedBottom}px`;
    bottom.style.height = `${clampedBottom}px`;
  });
  observer.observe(el);

  cleanup(() => {
    observer.disconnect();
    instance.destroy();
  });
};
export default split;
